import React, { useState } from "react";
import {
  LayoutDashboard, Database, Upload, LogOut, ChevronRight,
  Sparkles, Bell, Settings, User, Menu, X, Brain, ShieldCheck
} from "lucide-react";
import "./MainLayout.css";
import logo from "../BoomerangLogoA.jpg";
import { APP_VERSION, CHANGELOG } from "../version";
import ProjectsNav from "../components/ProjectsNav";
import ProjectBreadcrumb from "../components/ProjectBreadcrumb";

const NAV_ITEMS = [
  {
    // AI Insights is a NAV GROUP (roadmap #77): a parent header with two sub-items — Analyze
    // (the ask/chat page, tab id "dashboard" kept for back-compat) and Dashboard (the board home,
    // tab id "ai-dashboard"). Children inherit the group's visibleTo.
    id: "ai-insights-group",
    label: "AI Insights",
    icon: <Brain size={18} />,
    desc: "Natural language data analysis",
    visibleTo: ["FullUser", "LightUser"],
    children: [
      { id: "dashboard", label: "Analyze", icon: <Sparkles size={16} />, desc: "Ask questions, build charts" },
      { id: "ai-dashboard", label: "Dashboard", icon: <LayoutDashboard size={16} />, desc: "Your board + pinned charts" },
    ],
  },
  {
    id: "s3-upload",
    label: "File Upload",
    icon: <Upload size={18} />,
    badge: null,
    desc: "Upload files directly to S3",
    visibleTo: ["LightUser"],
  },
  {
    id: "database-setup",
    label: "Database Setup",
    icon: <Database size={18} />,
    badge: null,
    desc: "Schema detection & table creation",
    visibleTo: ["FullUser"],
  },
  {
    id: "data-upload",
    label: "Data Upload",
    icon: <Upload size={18} />,
    badge: null,
    desc: "Upload, clean & load data",
    visibleTo: ["FullUser"],
  },
];

export default function MainLayout({ user, activeTab, onTabChange, onLogout, tenantType, isSuperAdmin, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  // "What's New" defaults to the 5 most recent releases; "Show full history" reveals the rest.
  // Reset every time the modal is reopened so it never opens mid-history from a prior session.
  const [showFullHistory, setShowFullHistory] = useState(false);
  const RECENT_RELEASES_COUNT = 5;

  // ── Environment badge (build-time REACT_APP_ENV) ──────────────────────────
  // Prevents "am I in Prod?" mistakes now that Dev + Prod are separate accounts.
  // DEV: prominent amber pill shown to everyone. PROD: subtle green pill shown to
  // admins only (regular customers never see it). Default = production (safe).
  const APP_ENV = (process.env.REACT_APP_ENV || "production").toLowerCase();
  const isProdEnv = APP_ENV === "production" || APP_ENV === "prod";
  // Dev: cue shown to everyone. Prod: shown to admins only (customers see nothing extra).
  const showEnvBadge = !isProdEnv || isSuperAdmin;

  // Filter nav items based on tenant type
  // While loading (tenantType is null), show only Dashboard as a safe default
  const visibleNavItems = NAV_ITEMS.filter(
    (item) => tenantType ? item.visibleTo.includes(tenantType) : item.id === "ai-insights-group"
  );
  // Flattened list (groups → their children) for looking up the active tab's label in the topbar.
  const flatNav = visibleNavItems.flatMap((n) => (n.children ? n.children : [n]));

  return (
    <div className="layout-root">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="layout-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`layout-sidebar ${sidebarOpen ? "layout-sidebar-open" : ""}`}>
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-logo">
            <Database size={22} />
            <Sparkles size={11} className="sidebar-logo-spark" />
          </div>
          <div className="sidebar-brand-text" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div>
              <span className="sidebar-brand-name">Data Uploader</span>
              <span className="sidebar-brand-ai">AI</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
              <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>Powered by</span>
              <img src={logo} alt="Boomerang Logo" style={{ height: '24px', objectFit: 'contain' }} />
            </div>
          </div>
          <button
            className="sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          <div className="sidebar-nav-label">Navigation</div>
          {/* "File Upload" (s3-upload) is rendered AFTER "Manage projects" below (user request:
              file upload belongs with project management, not above it) — every other nav item
              keeps its original position/order. */}
          {visibleNavItems.filter((item) => item.id !== "s3-upload").map((item) => (
            item.children ? (
              /* Nav group (e.g. AI Insights): a parent header + indented sub-items (#77). */
              <div key={item.id} style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px 4px", color: "#94a3b8", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" }}>
                  <span className="sidebar-nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
                {item.children.map((sub) => (
                  <button
                    key={sub.id}
                    className={`sidebar-nav-item ${activeTab === sub.id ? "sidebar-nav-item-active" : ""}`}
                    style={{ marginLeft: "24px", borderLeft: "1px solid rgba(148,163,184,.25)", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    onClick={() => { onTabChange(sub.id); setSidebarOpen(false); }}
                  >
                    <span className="sidebar-nav-icon">{sub.icon}</span>
                    <span className="sidebar-nav-content">
                      <span className="sidebar-nav-item-label">{sub.label}</span>
                      <span className="sidebar-nav-item-desc">{sub.desc}</span>
                    </span>
                    {activeTab === sub.id && <ChevronRight size={14} className="sidebar-nav-chevron" />}
                  </button>
                ))}
              </div>
            ) : (
              <button
                key={item.id}
                className={`sidebar-nav-item ${activeTab === item.id ? "sidebar-nav-item-active" : ""}`}
                onClick={() => { onTabChange(item.id); setSidebarOpen(false); }}
              >
                <span className="sidebar-nav-icon">{item.icon}</span>
                <span className="sidebar-nav-content">
                  <span className="sidebar-nav-item-label">{item.label}</span>
                  <span className="sidebar-nav-item-desc">{item.desc}</span>
                </span>
                {activeTab === item.id && <ChevronRight size={14} className="sidebar-nav-chevron" />}
              </button>
            )
          ))}

          {/* Divider — visually separates the tools (Analyze/Data) from Projects */}
          <div className="sidebar-divider" style={{ margin: "12px 6px 10px" }} />

          {/* Projects — always-visible active-project indicator + switcher (R17) */}
          <ProjectsNav onNavigate={() => setSidebarOpen(false)} />

          {/* File Upload — moved here (below Manage projects) per user request; same
              button markup/behavior as before, just relocated. */}
          {visibleNavItems.filter((item) => item.id === "s3-upload").map((item) => (
            <button
              key={item.id}
              className={`sidebar-nav-item ${activeTab === item.id ? "sidebar-nav-item-active" : ""}`}
              onClick={() => { onTabChange(item.id); setSidebarOpen(false); }}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              <span className="sidebar-nav-content">
                <span className="sidebar-nav-item-label">{item.label}</span>
                <span className="sidebar-nav-item-desc">{item.desc}</span>
              </span>
              {activeTab === item.id && <ChevronRight size={14} className="sidebar-nav-chevron" />}
            </button>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="sidebar-bottom">
          <div className="sidebar-divider" />
          {/* Security — available to every signed-in user */}
          <button
            className={`sidebar-nav-item sidebar-settings ${activeTab === 'security' ? 'sidebar-nav-item-active' : ''}`}
            onClick={() => { onTabChange("security"); setSidebarOpen(false); }}
          >
            <span className="sidebar-nav-icon"><ShieldCheck size={18} /></span>
            <span className="sidebar-nav-content">
              <span className="sidebar-nav-item-label">Security</span>
            </span>
          </button>
          {/* Settings/onboarding is restricted to super admins (the original testuser). */}
          {isSuperAdmin && (
            <button 
              className={`sidebar-nav-item sidebar-settings ${activeTab === 'admin' ? 'sidebar-nav-item-active' : ''}`}
              onClick={() => { onTabChange("admin"); setSidebarOpen(false); }}
            >
              <span className="sidebar-nav-icon"><Settings size={18} /></span>
              <span className="sidebar-nav-content">
                <span className="sidebar-nav-item-label">Settings</span>
              </span>
            </button>
          )}

          {/* User card */}
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">{user?.initials || "U"}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name || "User"}</div>
              <div className="sidebar-user-email">{user?.email || ""}</div>
            </div>
            <button
              className="sidebar-logout"
              onClick={() => onLogout()}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>

          {/* Version badge — click to see changelog */}
          <button
            onClick={() => { setShowFullHistory(false); setShowChangelog(true); }}
            style={{
              width: "100%", marginTop: "8px", padding: "9px 12px",
              background: "linear-gradient(135deg, #6366f1, #7c3aed)",
              border: "none", borderRadius: "999px", cursor: "pointer",
              fontSize: "12px", color: "#fff", fontWeight: 800, textAlign: "center",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
              boxShadow: "0 4px 14px rgba(99,102,241,.40)", letterSpacing: ".2px",
            }}
            title="View release notes"
          >
            <Sparkles size={13} />
            <span>v{APP_VERSION}</span>
            <span style={{ fontWeight: 500, opacity: .85 }}>· Release notes</span>
          </button>
        </div>
      </aside>

      {/* Changelog modal */}
      {showChangelog && (
        <div
          onClick={() => setShowChangelog(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 10000,
            background: "rgba(0,0,0,0.5)", display: "flex",
            alignItems: "center", justifyContent: "center", padding: "20px",
            backdropFilter: "blur(3px)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: "16px", width: "100%", maxWidth: "480px",
              maxHeight: "80vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{
              padding: "20px 24px", borderBottom: "1px solid #e2e8f0",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              position: "sticky", top: 0, background: "#fff", borderRadius: "16px 16px 0 0",
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#0f172a" }}>What's New</h3>
                <span style={{ fontSize: "12px", color: "#6366f1", fontWeight: 600 }}>Current version: v{APP_VERSION}</span>
              </div>
              <button onClick={() => setShowChangelog(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: "20px 24px" }}>
              {(showFullHistory ? CHANGELOG : CHANGELOG.slice(0, RECENT_RELEASES_COUNT)).map((rel, i) => (
                <div key={rel.version} style={{ marginBottom: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <span style={{
                      fontSize: "12px", fontWeight: 700, color: "#fff",
                      background: i === 0 ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : "#94a3b8",
                      padding: "2px 10px", borderRadius: "12px",
                    }}>
                      v{rel.version}
                    </span>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>{rel.date}</span>
                    {i === 0 && <span style={{ fontSize: "10px", color: "#10b981", fontWeight: 600 }}>● Latest</span>}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: "18px" }}>
                    {rel.notes.map((note, ni) => (
                      <li key={ni} style={{ fontSize: "12.5px", color: "#475569", lineHeight: 1.6, marginBottom: "3px" }}>{note}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {/* Full history toggle — CHANGELOG has grown to 100+ releases, so the modal defaults
                  to the 5 most recent (the vast majority of opens are "what just changed") and
                  reveals the rest on demand instead of always rendering the whole list. */}
              {CHANGELOG.length > RECENT_RELEASES_COUNT && (
                <button
                  onClick={() => setShowFullHistory((v) => !v)}
                  style={{
                    display: "block", width: "100%", marginTop: "4px", padding: "9px 12px",
                    borderRadius: "9px", border: "1px solid #e2e8f0", background: "#f8fafc",
                    color: "#4f46e5", fontSize: "12.5px", fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {showFullHistory
                    ? "Show less"
                    : `Show full history (${CHANGELOG.length - RECENT_RELEASES_COUNT} more releases)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="layout-main">
        {/* Top bar */}
        <header className="layout-topbar" style={{ position: 'relative' }}>
          <button
            className="topbar-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>

          <div className="topbar-breadcrumb" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* On AI Insights the on-page "Working in" bar is the project indicator/switcher,
                so the breadcrumb shows just the page name there (no duplication — Option B).
                Every other page keeps the Company › Project "you are here". */}
            {activeTab !== "dashboard" && activeTab !== "ai-dashboard" && (
              <>
                <ProjectBreadcrumb />
                <ChevronRight size={14} className="topbar-sep" />
              </>
            )}
            <span className="topbar-page">
              {flatNav.find((n) => n.id === activeTab)?.label || "Settings"}
            </span>
          </div>

          <div className="topbar-actions">
            {showEnvBadge && (
              // Smooth, text-free environment cue: a small status dot with a soft ring.
              // Amber = Development, emerald = Production. The environment name lives only in
              // the hover tooltip — builders recognize the color at a glance; customers just
              // see a subtle, on-brand dot (no "DEV/PROD" wording).
              <span
                title={`Environment: ${isProdEnv ? "Production" : "Development"}`}
                aria-label={`Environment: ${isProdEnv ? "Production" : "Development"}`}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: "16px", height: "16px", marginRight: "10px", flex: "0 0 auto",
                }}
              >
                <span
                  style={{
                    width: "9px", height: "9px", borderRadius: "999px",
                    background: isProdEnv ? "#10b981" : "#f59e0b",
                    boxShadow: `0 0 0 3px ${isProdEnv ? "rgba(16,185,129,0.18)" : "rgba(245,158,11,0.18)"}`,
                  }}
                />
              </span>
            )}
            <button className="topbar-icon-btn" aria-label="Notifications">
              <Bell size={18} />
              <span className="topbar-notif-dot" />
            </button>
            <div className="topbar-avatar">{user?.initials || "U"}</div>
          </div>
        </header>

        {/* Page content */}
        <main className="layout-content animate-fade">
          {children}
        </main>
      </div>
    </div>
  );
}
